import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * TESTIMONIALS — new variants (two-by-two-grid · violet-2up ·
 * happy-clients-quadgrid · single-quote-portrait · author-bottom-card)
 *
 * Iter 175c — Section Audit. title and every item's quote · author ·
 * role are inline-editable. Source field resolves to `items` or
 * `testimonials`, preserving content when swapping variants.
 * ════════════════════════════════════════════════════════════════════════ */
export function TestimonialsExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout;
  const sourceField = Array.isArray(c.items) ? 'items' : 'testimonials';
  const items = (c[sourceField] || []).slice(0, 8);
  const seed = ['Sarah K.', 'Mike L.', 'Jamie R.', 'Alex P.'];

  if (layout === 'two-by-two-grid') {
    const reviews = items.length ? items : seed.map((n)=>({ author:n, quote:'Excellent work — would 100% recommend.', role:'Client' }));
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Testimonials'} tag="h2" className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {reviews.slice(0,4).map((r,i)=>(
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-300 to-fuchsia-400 flex-shrink-0" />
                <div>
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.quote`} value={r.quote || 'Great experience.'} tag="p" className="text-sm leading-relaxed opacity-85" placeholder="Quote" />
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.author`} value={r.author} tag="p" className="mt-3 text-xs uppercase tracking-[0.18em] font-bold" style={{ color: theme.accent }} placeholder="Author" />
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.role`} value={r.role || ''} tag="p" className="text-xs opacity-60" placeholder="Role" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'violet-2up') {
    const reviews = items.length ? items : seed.map((n)=>({ author:n, quote:'A business to give big high net worth individuals real time.', role:'Client' }));
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-8 py-16" style={{ background: '#3B1F8B' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 text-white">
          {reviews.slice(0,2).map((r,i)=>(
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-white/20 flex-shrink-0" />
              <div>
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.quote`} value={r.quote} tag="p" className="text-sm leading-relaxed opacity-95" placeholder="Quote" />
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.author`} value={r.author} tag="p" className="mt-4 text-xs uppercase tracking-[0.18em] font-bold opacity-70" placeholder="Author" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (layout === 'happy-clients-quadgrid') {
    const reviews = items.length ? items : seed.map((n)=>({ author:n, quote:'Excellent service, would use again.', role:'Client' }));
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-8 py-20" style={{ background: '#3B1F8B' }}>
        <div className="max-w-5xl mx-auto text-white">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Our Happy Clients'} tag="h2" className="text-3xl md:text-4xl font-bold mb-10" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {reviews.slice(0,4).map((r,i)=>(
              <div key={i} className="border border-white/15 rounded-xl p-5 flex gap-4 bg-white/5">
                <div className="w-10 h-10 rounded-full bg-white/20 flex-shrink-0" />
                <div>
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.quote`} value={r.quote} tag="p" className="text-sm opacity-90 leading-relaxed" placeholder="Quote" />
                  <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.author`} value={r.author} tag="p" className="mt-3 text-xs uppercase tracking-[0.18em] font-bold opacity-70" placeholder="Author" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'single-quote-portrait') {
    const r = items[0] || { author: 'Sarah K.', quote: 'Best decision I made all year.', role: 'CEO, Acme Co.' };
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2 flex justify-center">
            <div className="w-48 h-48 rounded-full overflow-hidden bg-gradient-to-br from-violet-300 to-fuchsia-400">
              {r.photo && <SafeImg src={r.photo} alt={r.author} className="w-full h-full object-cover" />}
            </div>
          </div>
          <div className="md:col-span-3 text-center md:text-left">
            <E block={block} onEdit={onEdit} field={`${sourceField}.0.quote`} value={r.quote} tag="p" className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily: theme.headingFont }} placeholder="Quote" />
            <E block={block} onEdit={onEdit} field={`${sourceField}.0.author`} value={r.author} tag="p" className="mt-6 text-sm uppercase tracking-[0.18em] font-bold" style={{ color: theme.accent }} placeholder="Author" />
            <E block={block} onEdit={onEdit} field={`${sourceField}.0.role`} value={r.role || ''} tag="p" className="text-xs opacity-60 mt-0.5" placeholder="Role" />
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'author-bottom-card') {
    const reviews = items.length ? items : seed.map(n=>({ author:n, quote:'Solid experience, would recommend.', role:'Client' }));
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || ''} tag="h2" className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: theme.headingFont }} placeholder="Headline (optional)" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.slice(0,3).map((r,i)=>(
              <div key={i} className="p-8 rounded-2xl border border-current/10" style={{ borderRadius: theme.radius || 16 }}>
                <p className="text-3xl opacity-30 leading-none mb-4" style={{ color: theme.accent }}>&ldquo;</p>
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.quote`} value={r.quote} tag="p" className="text-sm opacity-85 leading-relaxed" placeholder="Quote" />
                <div className="mt-6 flex items-center gap-3 pt-4 border-t border-current/10">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-300 to-fuchsia-400">{r.photo && <SafeImg src={r.photo} alt={r.author} className="w-full h-full object-cover rounded-full" />}</div>
                  <div>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.author`} value={r.author} tag="p" className="text-xs font-bold" placeholder="Author" />
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.role`} value={r.role || ''} tag="p" className="text-[10px] opacity-60" placeholder="Role" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }
  // ── Iter 189c Phase B · Testimonials — NEW `wall-of-love` ──
  // Dense wall of 12 short testimonial cards (masonry-style 3-col grid with
  // varied heights). Uses a subtle white bg with hover lift. Ideal for
  // SaaS wanting to show breadth of positive user sentiment at a glance.
  if (layout === 'wall-of-love') {
    const wallItems = (c[sourceField] || []).slice(0, 12);
    // Pad to 6 with placeholder items so the grid always looks full
    const padded = wallItems.length >= 6 ? wallItems : [...wallItems, ...Array(Math.max(0, 6 - wallItems.length)).fill({ quote: 'Amazing team, would recommend.', author: 'Anon' })];
    return (
      <section className="px-6 py-24" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto text-center mb-14">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }}>{c.eyebrow || 'LOVED BY THE CRAFT'}</p>
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'A wall of love.'} tag="h2" className="mt-3 text-4xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Wall of Love" />
        </div>
        <div className="max-w-6xl mx-auto columns-1 md:columns-2 lg:columns-3 gap-5">
          {padded.map((it, i) => {
            const stars = Math.max(0, Math.min(5, Number(it.stars ?? 5)));
            return (
              <div key={i} className="mb-5 break-inside-avoid p-6 rounded-2xl transition-transform hover:-translate-y-1" style={{ background: theme.bg, border: `1px solid ${theme.ink}12`, boxShadow: '0 2px 8px -4px rgba(0,0,0,0.08)' }}>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: stars }).map((_, si) => (
                    <svg key={si} width={12} height={12} viewBox="0 0 12 12"><path d="M6 0l1.7 4 4.3.3-3.3 2.7 1 4.2L6 9l-3.7 2.2 1-4.2L0 4.3l4.3-.3z" fill="#F59E0B"/></svg>
                  ))}
                </div>
                <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.quote`} value={it.quote} tag="p" className="text-sm leading-relaxed opacity-90" placeholder="Testimonial" />
                <div className="mt-4 flex items-center gap-2.5">
                  {it.image_url && <SafeImg src={it.image_url} alt="" className="w-8 h-8 rounded-full object-cover" />}
                  <div>
                    <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.author`} value={it.author} tag="p" className="text-xs font-bold" placeholder="Author" />
                    {it.role && <E block={block} onEdit={onEdit} field={`${sourceField}.${i}.role`} value={it.role} tag="p" className="text-[10px] opacity-60" placeholder="Role" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  // ── Iter 189c Phase B · Testimonials — NEW `interview-hero` ──
  // Long-form testimonial styled as a magazine interview: 2-column layout
  // with big Playfair-serif Q, big A, portrait photo + byline + press
  // publication logo. Adds "Q." and "A." labels typographically. Ideal
  // for high-consideration purchases needing a deep credibility beat.
  if (layout === 'interview-hero') {
    const item = (c[sourceField] || [])[0] || {
      question: 'How did the rebrand change your business?',
      answer:  'It was night and day. Within two months we went from being one of a dozen competitors to being the one people mentioned by name. The site alone drove a 3× conversion lift — and that\'s before the press coverage started.',
      author: 'Amelia Ross', role: 'Founder, Kindred Studios', image_url: '',
      publication: 'FEATURED IN — MONOCLE 24',
    };
    return (
      <section className="px-6 py-28" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-16 items-start">
          <div>
            {item.image_url
              ? <SafeImg src={item.image_url} alt={item.author} className="w-full aspect-[4/5] object-cover" style={{ borderRadius: theme.radius || 6 }} />
              : <div className="w-full aspect-[4/5]" style={{ background: 'linear-gradient(160deg,#F5F5F4,#E7E5E4)', borderRadius: theme.radius || 6 }} />}
            <p className="mt-4 text-[10px] uppercase tracking-[0.3em] font-bold opacity-70">{item.publication || c.publication || 'FEATURED IN'}</p>
          </div>
          <div className="md:pt-6">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: theme.accent }}>{c.eyebrow || 'INTERVIEW'}</p>
            <div className="mt-6 flex items-start gap-4">
              <span className='text-4xl font-bold opacity-30 font-["Playfair_Display","Georgia",serif]'>Q.</span>
              <E block={block} onEdit={onEdit} field={`${sourceField}.0.question`} value={item.question} tag="h3" className='text-2xl md:text-3xl font-bold leading-tight font-["Playfair_Display","Georgia",serif]' placeholder="The question" />
            </div>
            <div className="mt-6 flex items-start gap-4">
              <span className='text-4xl font-bold font-["Playfair_Display","Georgia",serif]' style={{ color: theme.accent }}>A.</span>
              <E block={block} onEdit={onEdit} field={`${sourceField}.0.answer`} value={item.answer} tag="p" className="text-lg leading-relaxed opacity-90" placeholder="The answer" />
            </div>
            <div className="mt-10 pt-6 border-t" style={{ borderColor: theme.ink + '18' }}>
              <E block={block} onEdit={onEdit} field={`${sourceField}.0.author`} value={item.author} tag="p" className="text-sm font-bold" placeholder="Author" />
              <E block={block} onEdit={onEdit} field={`${sourceField}.0.role`} value={item.role} tag="p" className="text-xs opacity-60 mt-0.5" placeholder="Role, Company" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return null;
}
